import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import {
  MessageSquare,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
  Phone,
  Mail,
} from "lucide-react";
import Layout from "../../components/Layout";
import { useTheme } from "../../contexts/ThemeContext";
import { leadContatoAPI } from "../../services/api";

const STATUS_OPTIONS = [
  { value: "novo", label: "Novo", color: "bg-blue-500/20 text-blue-500" },
  { value: "contatado", label: "Contatado", color: "bg-yellow-500/20 text-yellow-600" },
  { value: "qualificado", label: "Qualificado", color: "bg-purple-500/20 text-purple-500" },
  { value: "convertido", label: "Convertido", color: "bg-green-500/20 text-green-500" },
  { value: "descartado", label: "Descartado", color: "bg-gray-500/20 text-gray-500" },
];

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("pt-BR") + " " + d.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const statusBadge = (status) => {
  return STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
};

const AdminLeadsContato = () => {
  const { darkMode } = useTheme();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [loading, setLoading] = useState(true);
  const [resumo, setResumo] = useState([]);

  const [filterStatus, setFilterStatus] = useState("");
  const [filterUtmSource, setFilterUtmSource] = useState("");
  const [search, setSearch] = useState("");

  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (filterStatus) params.status = filterStatus;
      if (filterUtmSource) params.utm_source = filterUtmSource;
      if (search) params.search = search;

      const [listRes, resumoRes] = await Promise.all([
        leadContatoAPI.list(params),
        leadContatoAPI.resumo({}),
      ]);
      setRows(listRes.data?.rows || []);
      setTotal(listRes.data?.total || 0);
      setResumo(resumoRes.data || []);
    } catch (err) {
      console.error("Erro ao carregar leads:", err);
      toast.error("Erro ao carregar leads");
    } finally {
      setLoading(false);
    }
  }, [page, limit, filterStatus, filterUtmSource, search]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpdate = async (id, data) => {
    try {
      await leadContatoAPI.update(id, data);
      toast.success("Lead atualizado");
      await load();
      setEditing(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erro ao atualizar");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const cardClass = darkMode ? "bg-zinc-800 border-zinc-700" : "bg-white border-gray-200";
  const inputClass = darkMode
    ? "bg-zinc-700 border-gray-600 text-white"
    : "bg-white border-gray-300 text-gray-900";

  const totalNovos = resumo.find((r) => r.status === "novo")?.total || 0;
  const totalContatados = resumo.find((r) => r.status === "contatado")?.total || 0;
  const totalConvertidos = resumo.find((r) => r.status === "convertido")?.total || 0;
  const totalGeral = resumo.reduce((sum, r) => sum + Number(r.total), 0);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <MessageSquare className={darkMode ? "text-gray-200" : "text-gray-700"} />
            <h1 className={`text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>
              Leads do formulário
            </h1>
            <span className={`ml-2 text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
              {totalGeral} no total
            </span>
          </div>
          <button
            onClick={load}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm ${
              darkMode ? "bg-zinc-700 text-gray-200 hover:bg-zinc-600" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            <RefreshCw size={14} /> Atualizar
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className={`p-4 rounded-lg border ${cardClass}`}>
            <div className={`text-xs uppercase tracking-wide ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
              Novos (não contatados)
            </div>
            <div className={`text-2xl font-bold mt-1 ${darkMode ? "text-white" : "text-gray-900"}`}>
              {totalNovos}
            </div>
          </div>
          <div className={`p-4 rounded-lg border ${cardClass}`}>
            <div className={`text-xs uppercase tracking-wide ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
              Em contato
            </div>
            <div className={`text-2xl font-bold mt-1 ${darkMode ? "text-white" : "text-gray-900"}`}>
              {totalContatados}
            </div>
          </div>
          <div className={`p-4 rounded-lg border ${cardClass}`}>
            <div className={`text-xs uppercase tracking-wide ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
              Convertidos
            </div>
            <div className="text-2xl font-bold mt-1 text-green-500">
              {totalConvertidos}
            </div>
          </div>
          <div className={`p-4 rounded-lg border ${cardClass}`}>
            <div className={`text-xs uppercase tracking-wide ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
              Taxa de conversão
            </div>
            <div className={`text-2xl font-bold mt-1 ${darkMode ? "text-white" : "text-gray-900"}`}>
              {totalGeral > 0 ? ((totalConvertidos / totalGeral) * 100).toFixed(1) : "0"}%
            </div>
          </div>
        </div>

        <div className={`p-4 rounded-lg border mb-4 ${cardClass}`}>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar nome, email, WhatsApp..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className={`w-full pl-10 pr-3 py-2 rounded-md border text-sm ${inputClass}`}
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setPage(1);
              }}
              className={`px-3 py-2 rounded-md border text-sm ${inputClass}`}
            >
              <option value="">Todos os status</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Filtrar por UTM source..."
              value={filterUtmSource}
              onChange={(e) => {
                setFilterUtmSource(e.target.value);
                setPage(1);
              }}
              className={`px-3 py-2 rounded-md border text-sm ${inputClass}`}
            />
            <button
              onClick={() => {
                setSearch("");
                setFilterStatus("");
                setFilterUtmSource("");
                setPage(1);
              }}
              className={`px-3 py-2 rounded-md text-sm ${
                darkMode ? "bg-zinc-700 text-gray-200 hover:bg-zinc-600" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Limpar filtros
            </button>
          </div>
        </div>

        <div className={`rounded-lg border overflow-hidden ${cardClass}`}>
          {loading ? (
            <div className={`p-8 text-center ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
              Carregando...
            </div>
          ) : rows.length === 0 ? (
            <div className={`p-8 text-center ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
              Nenhum lead encontrado.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className={darkMode ? "bg-zinc-900" : "bg-gray-50"}>
                    <tr>
                      <th className={`px-4 py-3 text-left ${darkMode ? "text-gray-300" : "text-gray-700"}`}>Data</th>
                      <th className={`px-4 py-3 text-left ${darkMode ? "text-gray-300" : "text-gray-700"}`}>Lead</th>
                      <th className={`px-4 py-3 text-left ${darkMode ? "text-gray-300" : "text-gray-700"}`}>Contato</th>
                      <th className={`px-4 py-3 text-left ${darkMode ? "text-gray-300" : "text-gray-700"}`}>Origem</th>
                      <th className={`px-4 py-3 text-left ${darkMode ? "text-gray-300" : "text-gray-700"}`}>Status</th>
                      <th className={`px-4 py-3 ${darkMode ? "text-gray-300" : "text-gray-700"}`}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const status = statusBadge(r.status);
                      return (
                        <tr key={r.id} className={`border-t ${darkMode ? "border-zinc-700" : "border-gray-200"}`}>
                          <td className={`px-4 py-3 whitespace-nowrap ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                            {formatDate(r.created_at)}
                          </td>
                          <td className="px-4 py-3">
                            <div className={`font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>{r.nome}</div>
                            {r.porte_empresa && (
                              <div className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                                {r.porte_empresa} funcionários
                              </div>
                            )}
                          </td>
                          <td className={`px-4 py-3 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                            <div className="flex items-center gap-1 text-xs">
                              <Mail size={12} /> <a href={`mailto:${r.email}`} className="hover:underline">{r.email}</a>
                            </div>
                            <div className="flex items-center gap-1 text-xs mt-1">
                              <Phone size={12} />
                              <a
                                href={`https://wa.me/55${(r.whatsapp || "").replace(/\D/g, "")}`}
                                target="_blank"
                                rel="noreferrer"
                                className="hover:underline text-green-600"
                              >
                                {r.whatsapp}
                              </a>
                            </div>
                          </td>
                          <td className={`px-4 py-3 text-xs ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                            {r.utm_source ? (
                              <>
                                <div className="font-medium">{r.utm_source}</div>
                                <div className={darkMode ? "text-gray-400" : "text-gray-500"}>
                                  {r.utm_campaign || "—"}
                                </div>
                              </>
                            ) : (
                              <span className="text-gray-400">direct</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${status.color}`}>
                              {status.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => setEditing(r)}
                              className="text-nomoz-orange hover:underline text-xs font-medium"
                            >
                              Detalhes
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className={`flex items-center justify-between px-4 py-3 border-t ${darkMode ? "border-zinc-700" : "border-gray-200"}`}>
                <span className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                  Página {page} de {totalPages} · {total} leads
                </span>
                <div className="flex gap-2">
                  <button
                    disabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                    className={`p-2 rounded ${
                      darkMode ? "bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50" : "bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
                    } ${darkMode ? "text-gray-200" : "text-gray-700"}`}
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <button
                    disabled={page >= totalPages}
                    onClick={() => setPage(page + 1)}
                    className={`p-2 rounded ${
                      darkMode ? "bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50" : "bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
                    } ${darkMode ? "text-gray-200" : "text-gray-700"}`}
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {editing && (
          <LeadDetailModal
            lead={editing}
            onClose={() => setEditing(null)}
            onUpdate={handleUpdate}
            darkMode={darkMode}
          />
        )}
      </div>
    </Layout>
  );
};

const LeadDetailModal = ({ lead, onClose, onUpdate, darkMode }) => {
  const [status, setStatus] = useState(lead.status);
  const [observacoes, setObservacoes] = useState(lead.observacoes || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onUpdate(lead.id, { status, observacoes });
    setSaving(false);
  };

  const overlayClass = "fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4";
  const modalClass = darkMode ? "bg-zinc-800 text-white" : "bg-white text-gray-900";
  const inputCls = darkMode
    ? "bg-zinc-700 border-gray-600 text-white"
    : "bg-white border-gray-300 text-gray-900";

  const whatsappClean = (lead.whatsapp || "").replace(/\D/g, "");

  return (
    <div className={overlayClass} onClick={onClose}>
      <div
        className={`${modalClass} rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-center justify-between px-6 py-4 border-b ${darkMode ? "border-zinc-700" : "border-gray-200"}`}>
          <h2 className="text-lg font-bold">{lead.nome}</h2>
          <button onClick={onClose} className="hover:opacity-70">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className={darkMode ? "text-gray-400" : "text-gray-500"}>Email</div>
              <div className="font-medium">
                <a href={`mailto:${lead.email}`} className="hover:underline">{lead.email}</a>
              </div>
            </div>
            <div>
              <div className={darkMode ? "text-gray-400" : "text-gray-500"}>WhatsApp</div>
              <div className="font-medium">
                <a
                  href={`https://wa.me/55${whatsappClean}`}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:underline text-green-600"
                >
                  {lead.whatsapp}
                </a>
              </div>
            </div>
            <div>
              <div className={darkMode ? "text-gray-400" : "text-gray-500"}>Porte</div>
              <div className="font-medium">{lead.porte_empresa || "—"}</div>
            </div>
            <div>
              <div className={darkMode ? "text-gray-400" : "text-gray-500"}>Recebido em</div>
              <div className="font-medium">{formatDate(lead.created_at)}</div>
            </div>
          </div>

          {lead.mensagem && (
            <div>
              <div className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"} mb-1`}>
                Mensagem do lead
              </div>
              <div
                className={`p-3 rounded text-sm ${darkMode ? "bg-zinc-900" : "bg-gray-50"} whitespace-pre-wrap`}
              >
                {lead.mensagem}
              </div>
            </div>
          )}

          <div className={`p-3 rounded text-sm ${darkMode ? "bg-zinc-900" : "bg-gray-50"}`}>
            <div className="font-medium mb-2">Origem</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>UTM source: <span className="font-medium">{lead.utm_source || "—"}</span></div>
              <div>Campanha: <span className="font-medium">{lead.utm_campaign || "—"}</span></div>
              <div>Medium: <span className="font-medium">{lead.utm_medium || "—"}</span></div>
              <div>gclid: <span className="font-medium truncate">{lead.gclid || "—"}</span></div>
              <div className="col-span-2 truncate">
                Landing: <span className="font-medium">{lead.landing_page || "—"}</span>
              </div>
            </div>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? "text-gray-200" : "text-gray-700"}`}>
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className={`w-full px-3 py-2 rounded-md border text-sm ${inputCls}`}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? "text-gray-200" : "text-gray-700"}`}>
              Observações internas
            </label>
            <textarea
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              rows={3}
              placeholder="Anotações da conversa, propostas enviadas, etc."
              className={`w-full px-3 py-2 rounded-md border text-sm ${inputCls}`}
            />
          </div>
        </div>

        <div className={`flex justify-end gap-2 px-6 py-3 border-t ${darkMode ? "border-zinc-700 bg-zinc-900" : "border-gray-200 bg-gray-50"}`}>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-md text-sm ${
              darkMode ? "bg-zinc-700 text-gray-200 hover:bg-zinc-600" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-md text-sm bg-nomoz-orange text-white hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminLeadsContato;
